import React, { useEffect, useMemo, useState } from "react";

import styles from "../styles/AdStatistics.scss";
import { Chart } from "react-charts";

import { useDispatch, useSelector } from "react-redux";
import { AdStatisticPromotionInterface } from "../../../typings/interfaces";
import { getIconUrl } from "../../../services/asset";
import { getAdStatistics, selectAdStatistics } from "../../../routes/Ad/ad.ducks";
import { selectUserInfo } from "../../../routes/Auth/auth.ducks";
import moment from "moment";
import { DatePicker, Icon, Select, Tooltip } from "antd";
import { PageRoute, START_PAGE_PARAM, PromotionFromPages, UserRole } from "../../../models";
import history from "../../../history";
import ReactGA from "react-ga4";
import { getPromotionMultiplier } from "../../../utils/promotionMultiplier";
import AdStatisticsLoader from "../AdStatisticsLoader";


export interface IAdStatisticsProps {
  adData: any;
  match?: { params: { id: string } };
}

const METRICS = [
  { key: "dailyViewCount", label: "Pregledi oglasa", tooltipLabel: "Pregleda", iconFile: "stat-view.svg", iconFileActive: "stat-view-red.svg", chartInfo: "Koliko puta su korisnici otvorili i pregledali vaš oglas.", emptyTitle: "Još nema pregleda oglasa", emptyTextPeriod: "U odabranom periodu oglas nema pregleda.", emptyText: "Čim ga korisnici otvore, ovdje ćete vidjeti kretanje po danima." },
  { key: "searchedViewCount", label: "Prikazi u pretrazi", tooltipLabel: "Prikaza", iconFile: "stat-search.svg", iconFileActive: "stat-search-red.svg", chartInfo: "Koliko puta se vaš oglas pojavio korisnicima u rezultatima pretrage.", emptyTitle: "Još nema prikaza u pretrazi", emptyTextPeriod: "U odabranom periodu oglas nije prikazan u pretrazi.", emptyText: "Čim se pojavi u rezultatima, ovdje ćete vidjeti kretanje po danima." },
  { key: "carouselScrollCount", label: "Pregledi fotografija u pretrazi", tooltipLabel: "Pregleda fotografija", iconFile: "stat-photo.svg", iconFileActive: "stat-photo-red.svg", chartInfo: "Koliko puta su korisnici pregledali fotografije oglasa iz rezultata pretrage.", emptyTitle: "Još nema pregleda fotografija", emptyTextPeriod: "U odabranom periodu nitko nije pregledao fotografije.", emptyText: "Čim ih netko pregleda, ovdje ćete vidjeti kretanje po danima." },
  { key: "phoneClicks", label: "Klikovi na telefon", tooltipLabel: "Klikova", iconFile: "stat-phone.svg", iconFileActive: "stat-phone-red.svg", chartInfo: "Koliko puta su korisnici kliknuli na broj telefona.", emptyTitle: "Još nema klikova na telefon", emptyTextPeriod: "U odabranom periodu nema klikova na telefon.", emptyText: "Čim netko klikne broj, ovdje ćete vidjeti kretanje po danima." },
  { key: "favoritesCount", label: "Spremanja oglasa", tooltipLabel: "Spremanja", iconFile: "stat-favorite.svg", iconFileActive: "stat-favorite-red.svg", chartInfo: "Koliko puta su korisnici spremili vaš oglas među favorite.", emptyTitle: "Oglas još nije spremljen", emptyTextPeriod: "U odabranom periodu oglas nije spremljen.", emptyText: "Čim ga netko spremi, ovdje ćete vidjeti kretanje po danima." },
  { key: "messageReceivedCount", label: "Primljene poruke", tooltipLabel: "Poruka", iconFile: "stat-message.svg", iconFileActive: "stat-message-red.svg", chartInfo: "Koliko poruka ste primili u vezi s oglasom.", emptyTitle: "Još nema primljenih poruka", emptyTextPeriod: "U odabranom periodu niste primili nijednu poruku.", emptyText: "Čim vam kupac pošalje upit, ovdje ćete vidjeti kretanje po danima." },
];

const GA_CATEGORY = "Statistika oglasa";

const sendGa = (label: string) => {
  ReactGA.event({ category: GA_CATEGORY, action: "Click", label });
};

const sumSeries = (series: any[]) =>
  Array.isArray(series) ? series.reduce((acc, d) => acc + (d.count || 0), 0) : 0;

const AdStatistics = (props: IAdStatisticsProps) => {
  const { adData } = props;

  const dispatch = useDispatch();
  const adStatistics = useSelector(selectAdStatistics);
  const user = useSelector(selectUserInfo);
  const isAdmin = !!user && (user.role === UserRole.admin || user.role === UserRole.superAdmin);
  const adId = (adData && adData.id) || (props.match && props.match.params && props.match.params.id);

  const [isLoading, setIsLoading] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState("dailyViewCount");
  const [periodMode, setPeriodMode] = useState("30");
  const [customRange, setCustomRange] = useState(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth <= 768
  );
  // Fiksni safe offset-i za pink overlay istaknutog perioda — moraju biti
  // >= plot area padding-a chart-a (`padding` opcija dolje), inace bi
  // overlay bjezao preko Y/X osi labela. Na mobitelu su chart padding-i
  // manji pa i overlay bounds moraju biti manji.
  const plotBounds = isMobile
    ? { left: 30, right: 12, top: 30, bottom: 68 }
    : { left: 64, right: 38, top: 30, bottom: 68 };

  useEffect(() => {
    setIsLoading(true);
    if (adId) {
      dispatch(getAdStatistics(adId, () => setIsLoading(false)));
    } else {
      setIsLoading(false);
    }
    sendGa("Prikazana stranica");
  }, [adId]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);


  const promotionStatusByDate = useMemo(() => {
    const map = new Map();
    if (adStatistics && adStatistics.dailyPromotionStatus) {
      adStatistics.dailyPromotionStatus.forEach((s: any) => {
        map.set(s.date, s.isPromoted);
      });
    }
    return map;
  }, [adStatistics]);

  const earliestDate = useMemo(() => {
    const source = adStatistics && adStatistics.dailyViewCount;
    if (!source || source.length === 0) return null;
    return moment(source[0].date);
  }, [adStatistics]);

  // Uzimamo max datum iz podataka umjesto "danas" da grafikon ne pokazuje
  // prazne dane kada BE jos nije registrirao pregled za tekuci dan.
  const latestDate = useMemo(() => {
    const source = adStatistics && adStatistics.dailyViewCount;
    if (!source || source.length === 0) return null;
    return moment(source[source.length - 1].date);
  }, [adStatistics]);

  const activeRange = useMemo(() => {
    if (!latestDate) return null;
    const cr: any = customRange;
    if (periodMode === "custom" && cr && cr[0] && cr[1]) {
      return { from: cr[0].clone(), to: cr[1].clone() };
    }
    const days = 30;
    const from = latestDate.clone().subtract(days - 1, "days");
    const clampedFrom = earliestDate && from.isBefore(earliestDate) ? earliestDate.clone() : from;
    return { from: clampedFrom, to: latestDate.clone() };
  }, [periodMode, customRange, latestDate, earliestDate]);

  const filterByRange = (series: any[]) => {
    if (!Array.isArray(series) || !activeRange) return [];
    return series.filter((d: any) => {
      const m = moment(d.date);
      return m.isSameOrAfter(activeRange.from, "day") && m.isSameOrBefore(activeRange.to, "day");
    });
  };

  const filteredByMetric = useMemo(() => {
    if (!adStatistics) return {};
    const out: any = {};
    METRICS.forEach(m => {
      out[m.key] = filterByRange(adStatistics[m.key]);
    });
    return out;
  }, [adStatistics, activeRange]);

  const totals = useMemo(() => {
    const out: any = {};
    METRICS.forEach(m => { out[m.key] = sumSeries(filteredByMetric[m.key]); });
    return out;
  }, [filteredByMetric]);

  const periodDays = useMemo(() => {
    if (!activeRange) return 0;
    return activeRange.to.diff(activeRange.from, "days") + 1;
  }, [activeRange]);

  const selectedTotal = totals[selectedMetric] || 0;
  const selectedAverage = periodDays > 0 ? selectedTotal / periodDays : 0;

  const chartSeries = useMemo(() => {
    if (!activeRange) return null;
    const series = filteredByMetric[selectedMetric] || [];
    // Mapiramo postojece podatke po YYYY-MM-DD ključu, pa punimo cijeli
    // aktivni raspon dan-po-dan (nula za dane bez zapisa). Time chart x
    // os pokriva raspon 1:1 s activeRange, sto omogucuje da overlay
    // positioning (koji koristi activeRange %) tocno sjedne. Kada metrika
    // nema uopce zapisa, series je [] pa se popunjava iskljucivo nulama —
    // chart se renderira s osima, a overlay iznad prikazuje empty-state.
    const byDay: any = {};
    series.forEach((d: any) => {
      byDay[moment(d.date).format("YYYY-MM-DD")] = d.count || 0;
    });
    const filled: any[] = [];
    const cursor = activeRange.from.clone().startOf("day");
    const end = activeRange.to.clone().startOf("day");
    while (cursor.isSameOrBefore(end, "day")) {
      const key = cursor.format("YYYY-MM-DD");
      filled.push({
        date: cursor.toDate(),
        count: byDay[key] || 0,
      });
      cursor.add(1, "day");
    }
    return [{ label: "count", data: filled }];
  }, [filteredByMetric, selectedMetric, activeRange]);

  // Racunamo left/width postotke za pink overlay istaknutog perioda.
  // Prikazujemo iskljucivo promocije koje u cijelosti spadaju unutar
  // aktivnog perioda — parcijalne (koje bi bjezale preko rubova/Y labela)
  // preskačemo, jer se u chart plot area ne moze pouzdano CSS-om
  // uskladiti.
  const promotionOverlays = useMemo(() => {
    if (!activeRange) return [];
    const rangeStart = activeRange.from.startOf("day").valueOf();
    const rangeEnd = activeRange.to.endOf("day").valueOf();
    // Overlay ne smije prijeci ni danasnji datum, ni najkasniji dan za
    // koji BE ima zabiljeznih pregleda; niti pocinjati prije najranijeg
    // takvog dana.
    const todayEnd = moment().endOf("day").valueOf();
    const lastData = latestDate ? latestDate.endOf("day").valueOf() : todayEnd;
    const firstData = earliestDate ? earliestDate.startOf("day").valueOf() : rangeStart;
    const maxEnd = Math.min(rangeEnd, todayEnd, lastData);
    const minStart = Math.max(rangeStart, firstData);
    const totalMs = rangeEnd - rangeStart;
    if (totalMs <= 0) return [];

    const overlays: { leftPct: number; widthPct: number; label: string }[] = [];
    if (adStatistics && adStatistics.promotions) {
      adStatistics.promotions.forEach((p: AdStatisticPromotionInterface) => {
        const start = Math.max(moment(p.startDate).valueOf(), minStart);
        const end = Math.min(moment(p.expirationDate).valueOf(), maxEnd);
        if (end <= start) return;
        overlays.push({
          leftPct: ((start - rangeStart) / totalMs) * 100,
          widthPct: ((end - start) / totalMs) * 100,
          label: "Istaknuto",
        });
      });
    }
    return overlays;
  }, [adStatistics, activeRange, latestDate, earliestDate]);

  const activePromotion = useMemo(() => {
    if (adStatistics && adStatistics.promotions && adStatistics.promotions.length) {
      return adStatistics.promotions.find((p: AdStatisticPromotionInterface) => moment(p.expirationDate) > moment());
    }
    return null;
  }, [adStatistics]);

  const viewAverages = useMemo(() => {
    const dvc = adStatistics && adStatistics.dailyViewCount;
    if (!dvc || dvc.length === 0) return { avgAll: null, avgPromoted: null };
    const totalAll = dvc.reduce((acc: number, d: any) => acc + d.count, 0);
    const promoted = dvc.filter((d: any) => promotionStatusByDate.get(d.date));
    const totalPromoted = promoted.reduce((acc: number, d: any) => acc + d.count, 0);
    return {
      avgAll: totalAll > 0 ? totalAll / dvc.length : null,
      avgPromoted: promoted.length > 0 && totalPromoted > 0 ? totalPromoted / promoted.length : null,
    };
  }, [adStatistics, promotionStatusByDate]);

  const handleMetricClick = (key: string, label: string) => {
    setSelectedMetric(key);
    sendGa("Odabrano " + label);
  };

  const handlePeriodMode = (mode: string) => {
    setPeriodMode(mode);
    if (mode !== "custom") setCustomRange(null);
    if (mode === "30") sendGa("Odabran period od 30 dana");
    else sendGa("Odabran custom period");
  };

  // Klik/promjena na bilo koji od dva datuma automatski prebacuje mod
  // na "custom" i zadrzava drugi datum iz trenutno aktivnog raspona.
  const handleCustomDate = (which: string, date: any) => {
    if (!date || !activeRange) return;
    const from = which === "from" ? date : activeRange.from.clone();
    const to = which === "to" ? date : activeRange.to.clone();
    const next: any = [from, to];
    setCustomRange(next);
    if (periodMode !== "custom") {
      setPeriodMode("custom");
      sendGa("Odabran custom period");
    }
  };

  const handleAdPromote = (e: any, ad: any, gaLabel: string) => {
    e.preventDefault();
    if (!ad) return;
    if (ad.awaitingPayment) {
      history.push(PageRoute.finance);
    } else {
      sendGa(gaLabel);
      history.push(PageRoute.promote + "/" + ad.id + "?" + START_PAGE_PARAM + "=" + PromotionFromPages.AdStatistics);
    }
  };

  const chartOptions: any = useMemo(() => {
    if (!chartSeries) return null;
    const points: any = chartSeries[0].data;
    const allZero = points.every((p: any) => (p.count || 0) === 0);
    const rawMax = Math.max(1, ...points.map((p: any) => p.count));
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawMax)));
    const norm = rawMax / magnitude;
    const step = norm > 5 ? magnitude * 2 : norm > 2 ? magnitude : magnitude / 2;
    const yHardMax = allZero ? 20 : Math.ceil(rawMax / step) * step + 2 * step;
    return {
      data: chartSeries,
      padding: isMobile
        ? { left: 24, right: 8, top: 10, bottom: 30 }
        : { left: 40, right: 20, top: 10, bottom: 30 },
      primaryAxis: {
        getValue: (d: any) => d.date,
        // `band` scale generates one tick per data point (one per day since
        // the series is filled day-by-day). `time` scale auto-interpolated
        // extra intra-day ticks (like 27.07 12:00) which all format to the
        // same "27.07." label and looked like duplicates.
        scaleType: "band",
        showGrid: false,
        formatters: {
          scale: (d: any) => {
            // Thin the visible day labels — react-charts renders one tick
            // per band, but at wide ranges (custom period spanning months)
            // per-day labels overlap and are unreadable. Show only every
            // Nth day so the total count stays around `maxLabels`; ticks
            // themselves stay, just the labels are blank in between.
            const maxLabels = isMobile ? 6 : 10;
            const total = points.length;
            const step = Math.max(1, Math.ceil(total / maxLabels));
            const index = points.findIndex((p: any) => p.date.getTime() === d.getTime());
            if (index < 0) return "";
            const isLast = index === total - 1;
            const shouldShow = index % step === 0 || isLast;
            return shouldShow ? moment(d).format("DD.MM.") : "";
          },
          tooltip: (d: any) => moment(d).format("DD.MM.YYYY."),
        },
      },
      secondaryAxes: [{
        getValue: (d: any) => d.count,
        elementType: "area",
        hardMin: 0,
        hardMax: yHardMax,
        showGrid: true,
        formatters: {
          scale: (v: any) => (v == null ? "" : Math.round(v).toString()),
        },
      }],
      primaryCursor: { show: false, showLabel: false },
      secondaryCursor: { show: false, showLabel: false },
      defaultColors: ["#f94144"],
      getSeriesStyle: () => ({
        line: { stroke: "#f94144", strokeWidth: 2 },
        area: { fill: "rgba(249, 65, 68, 0.12)" },
      }),
      tooltip: {
        align: "top",
        render: (rProps: any) => {
          if (!rProps.focusedDatum) return null;
          if (allZero) return null;
          const d = rProps.focusedDatum.originalDatum;
          return (
            <div
              style={{
                background: "#fff",
                border: "1px solid #eaeaea",
                borderRadius: "8px",
                padding: "8px 12px",
                fontSize: "13px",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                whiteSpace: "nowrap",
              }}
            >
              <span style={{ color: "#8c8c8c", fontWeight: 400 }}>
                {selectedMetricConfig.tooltipLabel + ":"}
              </span>
              <b style={{ color: "#3f3f3f", fontWeight: 700 }}>{d.count}</b>
            </div>
          );
        },
      },
    };
  }, [chartSeries, selectedMetric, isMobile]);

  const selectedMetricConfig = useMemo(
    () => METRICS.find(m => m.key === selectedMetric) || METRICS[0],
    [selectedMetric]
  );

  const promotionMultiplierValue = useMemo(
    () => (adData ? getPromotionMultiplier(adData.category, adData.module) : undefined),
    [adData]
  );

  const adPagePosition = adStatistics && adStatistics.adPagePosition;

  const positionSentence = adPagePosition ? (
    <React.Fragment>
      <b>{"Vaš oglas trenutačno se nalazi na " + adPagePosition + ". stranici kategorije."}</b>
      {" "}
    </React.Fragment>
  ) : null;

  const renderBannerCopy = () => {
    if (adPagePosition && promotionMultiplierValue) {
      return (
        <span>
          {positionSentence}
          {"Promocijom može dobiti "}
          <b>{promotionMultiplierValue + " puta"}</b>
          {" više pregleda i bolju poziciju u rezultatima."}
        </span>
      );
    }
    if (adPagePosition) {
      return (
        <span>
          {positionSentence}
          {"Promocijom će ostvariti bolju poziciju u rezultatima."}
        </span>
      );
    }
    if (promotionMultiplierValue) {
      return (
        <span>
          {"Promocijom ovaj oglas može dobiti "}
          <b>{promotionMultiplierValue + " puta"}</b>
          {" više pregleda."}
        </span>
      );
    }
    return null;
  };

  const bannerCopy = renderBannerCopy();
  const promotionCallout = adData && !activePromotion && bannerCopy && (
    <div className={styles.promoBanner}>
      <div className={styles.promoBannerText}>
        <img src={getIconUrl("promote.svg")} alt="" className={styles.promoBannerIcon} />
        {bannerCopy}
      </div>
      <a
        className={styles.promoBannerLink}
        onClick={(e) => handleAdPromote(e, adData, "Klik na promociju vrh stranice")}
      >
        {"Istakni oglas"} <Icon type="right" />
      </a>
    </div>
  );

  const rangePickerDisabled = (current: any) => {
    if (!current) return false;
    if (earliestDate && current.isBefore(earliestDate.clone().startOf("day"))) return true;
    if (latestDate && current.isAfter(latestDate.clone().endOf("day"))) return true;
    return false;
  };

  if (isLoading) {
    return <AdStatisticsLoader />;
  }

  return (
    <div className={styles.content}>
      <div className={styles.title}>
        {adData && adData.title
          ? 'Statistika oglasa "{0}"'.replace("{0}", adData.title)
          : "Statistika oglasa"}
      </div>

      <div className={styles.card}>
        {promotionCallout}

        <p className={styles.summaryInfo}>
          <Icon type="info-circle" />
          {" Odaberite pokazatelj za prikaz na grafikonu. Sve brojke prikazane su za odabrani period."}
        </p>

        {isMobile ? (
          <Select
            value={selectedMetric}
            onChange={(val: any) => {
              const cfg = METRICS.find(m => m.key === val);
              handleMetricClick(val, cfg ? cfg.label : val);
            }}
            className={styles.metricsSelect}
            dropdownClassName={styles.metricsDropdown}
            optionLabelProp="label"
          >
            {METRICS.map(m => (
              <Select.Option
                key={m.key}
                value={m.key}
                label={
                  <span className={styles.metricsTrigger}>
                    <img
                      src={getIconUrl(m.iconFileActive)}
                      alt=""
                      className={styles.metricsTriggerIcon}
                    />
                    <span>{m.label}</span>
                    <span className={styles.metricsTriggerSep}>{" · "}</span>
                    <span>{totals[m.key] || 0}</span>
                  </span>
                }
              >
                {/* Inline flex jer SCSS :global scoping unutar antd Select
                    li-ja nije radio pouzdano — count je ostajao stuck na
                    label bez razmaka. */}
                <div style={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center", gap: "16px" }}>
                  <span>{m.label}</span>
                  <b>{totals[m.key] || 0}</b>
                </div>
              </Select.Option>
            ))}
          </Select>
        ) : (
          <div className={styles.metricsGrid}>
            {METRICS.map(m => (
              <button
                key={m.key}
                type="button"
                className={styles.metricCard + " " + (selectedMetric === m.key ? styles.metricCardActive : "")}
                onClick={() => handleMetricClick(m.key, m.label)}
              >
                <img
                  src={getIconUrl(selectedMetric === m.key ? m.iconFileActive : m.iconFile)}
                  alt={m.label}
                  className={styles.metricIcon}
                />
                <span className={styles.metricLabel}>{m.label}</span>
                <span className={styles.metricValue}>{totals[m.key] || 0}</span>
              </button>
            ))}
          </div>
        )}

        <h3 className={styles.periodTitle}>{"Odaberite period"}</h3>
        <div className={styles.periodControls}>
          <button
            type="button"
            className={styles.periodBtn + " " + (periodMode === "30" ? styles.periodBtnActive : "")}
            onClick={() => handlePeriodMode("30")}
          >
            {"30 dana"}
          </button>
          <button
            type="button"
            className={styles.periodBtn + " " + (periodMode === "custom" ? styles.periodBtnActive : "")}
            onClick={() => handlePeriodMode("custom")}
          >
            {"Prilagođeni period"}
          </button>

          {activeRange && (
            <div
              className={
                styles.periodRange + " " +
                (periodMode !== "custom" ? styles.periodRangeDisabled : "")
              }
            >
              <span className={styles.periodDateWrap}>
                <span className={styles.periodDatePrefix}>{"od"}</span>
                <DatePicker
                  value={activeRange.from}
                  onChange={(date) => handleCustomDate("from", date)}
                  format="DD. MM. YYYY."
                  disabledDate={rangePickerDisabled}
                  disabled={periodMode !== "custom"}
                  allowClear={false}
                  suffixIcon={<span />}
                  className={styles.periodDate}
                />
              </span>
              <span className={styles.periodDateSep}>-</span>
              <span className={styles.periodDateWrap}>
                <span className={styles.periodDatePrefix}>{"do"}</span>
                <DatePicker
                  value={activeRange.to}
                  onChange={(date) => handleCustomDate("to", date)}
                  format="DD. MM. YYYY."
                  disabledDate={rangePickerDisabled}
                  disabled={periodMode !== "custom"}
                  allowClear={false}
                  suffixIcon={<span />}
                  className={styles.periodDate}
                />
              </span>
            </div>
          )}
        </div>

        <div className={styles.chartSection}>
          <div className={styles.chartHeader}>
            <div className={styles.chartTitleRow}>
              <h3 className={styles.chartTitle}>{selectedMetricConfig.label}</h3>
              <Tooltip title={selectedMetricConfig.chartInfo}>
                <Icon type="info-circle" className={styles.chartInfoIcon} />
              </Tooltip>
            </div>
            <div className={styles.chartSummary}>
              <div className={styles.chartSummaryItem}>
                <span className={styles.chartSummaryLabel}>{"Ukupno u periodu"}</span>
                <b className={styles.chartSummaryValue}>{selectedTotal}</b>
              </div>
              <div className={styles.chartSummarySep} />
              <div className={styles.chartSummaryItem}>
                <span className={styles.chartSummaryLabel}>{"Prosjek po danu"}</span>
                <b className={styles.chartSummaryValue}>{(Math.round(selectedAverage * 10) / 10).toString().replace(".", ",")}</b>
              </div>
            </div>
            {promotionOverlays.length > 0 && (
              <div className={styles.chartLegend}>
                <span className={styles.chartLegendSwatch} />
                <span>{"Oglas istaknut u pretrazi"}</span>
              </div>
            )}
          </div>

          <div className={styles.chartWrapper}>
            {selectedTotal > 0 && (
              <div
                className={styles.chartPlotOverlay}
                style={{
                  top: plotBounds.top,
                  left: plotBounds.left,
                  right: plotBounds.right,
                  bottom: plotBounds.bottom,
                }}
              >
                {promotionOverlays.map((o, i) => (
                  <div
                    key={i}
                    className={styles.promoOverlay}
                    style={{ left: o.leftPct + "%", width: o.widthPct + "%" }}
                  >
                    {o.widthPct > 10 && (
                      <span className={styles.promoOverlayLabel}>{o.label}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
            {chartOptions ? (
              <div className={styles.chartInner}>
                <Chart options={chartOptions} />
                {selectedTotal === 0 && (
                  <div
                    className={styles.chartEmptyOverlay}
                    style={{
                      top: plotBounds.top,
                      left: plotBounds.left,
                      right: plotBounds.right,
                      bottom: plotBounds.bottom,
                    }}
                  >
                    <img
                      src={getIconUrl(selectedMetricConfig.iconFile)}
                      alt=""
                      className={styles.chartEmptyOverlayIcon}
                    />
                    <h4 className={styles.chartEmptyOverlayTitle}>
                      {selectedMetricConfig.emptyTitle}
                    </h4>
                    <p className={styles.chartEmptyOverlayText}>
                      <span className={styles.chartEmptyPeriod}>
                        {selectedMetricConfig.emptyTextPeriod + " "}
                      </span>
                      {selectedMetricConfig.emptyText}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              !isLoading && (
                <p className={styles.chartEmpty}>{"Nema podataka za odabrani period."}</p>
              )
            )}
          </div>
        </div>

        {adData && !activePromotion && (promotionMultiplierValue || (adPagePosition && adPagePosition > 1)) && (
          <React.Fragment>
            <h3 className={styles.tipsTitle}>{"Savjeti za bolje efekte oglasa"}</h3>
            <p className={styles.tipsInfo}>{"Preporuke su temeljene na usporedbi sa sličnim uspješnijim oglasima iz iste kategorije."}</p>
            <div className={styles.tipsGrid}>
              <div className={styles.tipCard}>
                <div className={styles.tipCardBody}>
                  <img src={getIconUrl("tip-promote.svg")} alt="Promocija" className={styles.tipIcon} />
                  {promotionMultiplierValue ? (
                    <span>{"Promocijom ovaj oglas može dobiti "}<b>{promotionMultiplierValue + " puta"}</b>{" više pregleda."}</span>
                  ) : (
                    <span>{positionSentence}{"Promocijom će ostvariti bolju poziciju u rezultatima."}</span>
                  )}
                </div>
                <a
                  href="#"
                  className={styles.tipCardLink}
                  onClick={(e) => handleAdPromote(e, adData, "Klik na promociju ispod grafikona")}
                >
                  {"Istakni oglas"} <Icon type="right" />
                </a>
              </div>
            </div>
          </React.Fragment>
        )}

        {isAdmin && !isLoading && (
          <div className={styles.adminStats}>
            <h3 className={styles.adminStatsTitle}>{"Prosjek pregleda (admin pregled)"}</h3>
            <div className={styles.statCards}>
              <div className={styles.statCard + " " + (viewAverages.avgAll === null ? styles.statCardEmpty : "")}>
                <span className={styles.statCardLabel}>{"Prosječan broj pregleda oglasa"}</span>
                <div className={styles.statCardBody}>
                  <span className={styles.statCardValue}>
                    {viewAverages.avgAll !== null ? Math.round(viewAverages.avgAll) : 0}
                  </span>
                  <span className={styles.statCardUnit}>{"po danu"}</span>
                </div>
              </div>
              <div className={styles.statCard + " " + (viewAverages.avgPromoted === null ? styles.statCardEmpty : "")}>
                <span className={styles.statCardLabel}>{"Prosječan broj pregleda dok je oglas istaknut"}</span>
                <div className={styles.statCardBody}>
                  <span className={styles.statCardValue}>
                    {viewAverages.avgPromoted !== null ? Math.round(viewAverages.avgPromoted) : 0}
                  </span>
                  <span className={styles.statCardUnit}>{"po danu"}</span>
                  {viewAverages.avgPromoted === null && (
                    <span className={styles.statCardEmptyText}>
                      <Icon type="info-circle" />
                      {"Oglas još nije bio istaknut."}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

AdStatistics.defaultProps = {
  adData: null,
};

export default AdStatistics;



// WEBPACK FOOTER //
// ./src/containers/AdStatisticsPage/AdStatistics/index.tsx